import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaWhatsapp, FaPhone, FaEnvelope } from 'react-icons/fa';

const ContactPage = () => {
  const whatsappURL = `https://wa.me/2349098370120?text=Hello%20KambiHome%2C%20I%20would%20like%20to%20make%20an%20inquiry.`;

  return (
    <>
      <Helmet>
        <title>Contact KambiHome | WhatsApp, Phone & Email Support</title>
        <meta
          name="description"
          content="Get in touch with KambiHome. Reach our support team via WhatsApp, email, or phone. We’re here to help with your AliExpress-powered shopping experience."
        />
        <meta property="og:title" content="Contact KambiHome" />
        <meta property="og:description" content="Reach out to KambiHome for any questions or support needs." />
        <meta property="og:url" content="https://kambihome.com/contact" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-100 to-white py-16 text-center px-6 mt-16">
        <h1 className="text-4xl font-bold text-green-700 mb-4 animate-fade-in">Contact Us</h1>
        <p className="text-gray-700 text-lg max-w-2xl mx-auto">
          Have a question or need support? Reach out to our team via WhatsApp, email, or visit our office.
        </p>
      </section>

      {/* Info Grid */}
      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-10">
        {/* Contact Info Panel */}
        <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg">
          <h2 className="text-2xl font-semibold text-green-700 mb-4">Contact Information</h2>

          <div className="space-y-3 text-gray-700">
            <p className="flex items-center gap-3"><FaPhone /> <strong>Phone (NG):</strong> +2349098370120</p>
            <p className="flex items-center gap-3"><FaPhone /> <strong>Phone (US):</strong> +1 3024691130</p>
            <p className="flex items-center gap-3"><FaPhone /> <strong>Fax:</strong> +1 3024691140</p>
            <p className="flex items-center gap-3"><FaEnvelope /> <strong>Email:</strong> talktous@kambihome.com</p>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Office Address</h3>
            <p>35 Igando-Ikotun Road (Suite 19, Praise Plaza), opposite Fidelity Bank Igando Lagos</p>
          </div>
        </div>

        {/* WhatsApp + Map */}
        <div className="flex flex-col gap-6">
          <a
            href={whatsappURL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-600 hover:bg-green-700 transition text-white px-6 py-4 rounded-lg flex items-center justify-center text-lg font-semibold shadow-md"
          >
            <FaWhatsapp className="mr-2 text-2xl" />
            Chat with us on WhatsApp
          </a>

          <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-lg">
            <iframe
              title="KambiHome Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.414284788214!2d3.2431957!3d6.4690891!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b870461a25e03%3A0x344e6821b756eeb!2sKamsiparts%20Automotive%20Limited!5e0!3m2!1sen!2sng!4v1752912081930!5m2!1sen!2sng"
              width="100%"
              height="350"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full border-0"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQs Section with Accordion */}
      <section className="bg-gray-100 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-8">
            Frequently Asked Questions
          </h2>
          <FAQAccordion
            faqs={[
              {
                question: "Do you ship internationally?",
                answer:
                  "We offer access to a global product catalog from AliExpress, and shipping depends on the vendor’s availability to your region.",
              },
              {
                question: "Can I contact support outside business hours?",
                answer:
                  "Yes, our WhatsApp support is available 24/7. Email queries are answered within 24 hours.",
              },
              {
                question: "Where are you located?",
                answer:
                  "We operate in Nigeria and the United States. Visit either of our offices or reach us online.",
              },
            ]}
          />
        </div>
      </section>

      <Footer />
    </>
  );
};

const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="bg-white rounded-md shadow-md transition hover:shadow-lg"
        >
          <button
            className="w-full text-left p-4 flex justify-between items-center focus:outline-none"
            onClick={() => toggle(index)}
          >
            <span className="font-semibold text-gray-800">{faq.question}</span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                openIndex === index ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              openIndex === index ? "max-h-40 p-4 pt-0" : "max-h-0 px-4"
            }`}
          >
            <p className="text-gray-600">{faq.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContactPage;
